// Sprint 7P — owner-only EXTERNAL payment link config (App Detail). The
// LAUNCH-SAFE payment model: the owner publishes an off-platform payment link +
// instructions; the generated app shows it after a booking; the owner confirms
// each payment MANUALLY in Manage Bookings. Pomela holds no funds, stores no
// card data or processor secrets, and verifies NOTHING automatically — the panel
// says so plainly. This is NOT the creator-BYOK PaymentConnect (a different,
// more advanced path); it stores only a non-secret display link.
//
// Reads GET /v1/apps/:id/payment-config (public, non-secret), writes via
// PUT (creator session + apps ownership RLS; a non-owner save is rejected 403).
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { CreditCard, Check, Loader2, AlertCircle, ShieldQuestion } from 'lucide-preact';
import { t } from '../lib/i18n';

interface Config {
  paymentRequired: boolean;
  externalUrl: string | null;
  instructions: string | null;
  amountLabel: string | null;
  currencyLabel: string | null;
  // §Batch 2c — manual tax (flat %) + shipping (flat fee), applied to the cart total.
  taxRate?: number;
  taxLabel?: string | null;
  shippingFee?: number;
  shippingLabel?: string | null;
}

type Phase = { k: 'loading' } | { k: 'ready' } | { k: 'saving' } | { k: 'saved' } | { k: 'error'; msg: string } | { k: 'unavailable' };

export function PaymentLinkConfig({ appId }: { appId: string }): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });
  const [required, setRequired] = useState(false);
  const [url, setUrl] = useState('');
  const [amount, setAmount] = useState('');
  const [instructions, setInstructions] = useState('');
  // §Batch 2c — manual tax/shipping (strings for the inputs; blank → 0).
  const [tax, setTax] = useState('');
  const [taxLabel, setTaxLabel] = useState('');
  const [shipping, setShipping] = useState('');
  const [shippingLabel, setShippingLabel] = useState('');

  const load = useCallback(() => {
    setPhase({ k: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/payment-config`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) return setPhase({ k: 'error', msg: t('mgmt.pl.errLoad') });
        const data = (await r.json()) as { config?: Config };
        const cfg = data.config;
        if (cfg) {
          setRequired(!!cfg.paymentRequired);
          setUrl(cfg.externalUrl ?? '');
          setAmount(cfg.amountLabel ?? '');
          setInstructions(cfg.instructions ?? '');
          setTax(cfg.taxRate && cfg.taxRate > 0 ? String(cfg.taxRate) : '');
          setTaxLabel(cfg.taxLabel ?? '');
          setShipping(cfg.shippingFee && cfg.shippingFee > 0 ? String(cfg.shippingFee) : '');
          setShippingLabel(cfg.shippingLabel ?? '');
        }
        setPhase({ k: 'ready' });
      })
      .catch(() => setPhase({ k: 'error', msg: t('mgmt.pl.errLoad') }));
  }, [appId]);

  useEffect(() => { load(); }, [load]);

  const urlValid = !url.trim() || /^https:\/\/\S+$/i.test(url.trim());
  const taxValid = tax.trim() === '' || (isFinite(Number(tax)) && Number(tax) >= 0 && Number(tax) <= 100);
  const shipValid = shipping.trim() === '' || (isFinite(Number(shipping)) && Number(shipping) >= 0);
  const canSave = (!required || (!!url.trim() && urlValid)) && taxValid && shipValid;

  async function save(): Promise<void> {
    if (!canSave) return;
    setPhase({ k: 'saving' });
    try {
      const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}/payment-config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ paymentRequired: required, externalUrl: url.trim() || null, amountLabel: amount.trim() || null, instructions: instructions.trim() || null, taxRate: tax.trim() === '' ? 0 : Number(tax), taxLabel: taxLabel.trim() || null, shippingFee: shipping.trim() === '' ? 0 : Number(shipping), shippingLabel: shippingLabel.trim() || null })
      });
      if (r.status === 503) return setPhase({ k: 'unavailable' });
      if (r.status === 403) return setPhase({ k: 'error', msg: t('mgmt.pl.err403') });
      if (r.status === 400) {
        // server-provided detail is shown verbatim (functional string); only the fallback is localized
        const j = (await r.json().catch(() => ({}))) as { detail?: string };
        return setPhase({ k: 'error', msg: j.detail || t('mgmt.pl.err400') });
      }
      if (!r.ok) return setPhase({ k: 'error', msg: t('mgmt.pl.errSave') });
      setPhase({ k: 'saved' });
      setTimeout(() => setPhase((p) => (p.k === 'saved' ? { k: 'ready' } : p)), 2200);
    } catch {
      setPhase({ k: 'error', msg: t('mgmt.errConnShort') });
    }
  }

  return (
    <section aria-label={t('mgmt.pl.title')} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <CreditCard size={15} /> {t('mgmt.pl.title')}
        </span>
      </div>

      {phase.k === 'loading' && <div className="skeleton-shimmer" style={{ height: 120, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} aria-hidden="true" />}

      {phase.k === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.pl.unavailableT')}</span><span className="pf-mgmt-panel__s">{t('mgmt.pl.unavailableS')}</span></span></div>
      )}

      {(phase.k === 'ready' || phase.k === 'saving' || phase.k === 'saved' || phase.k === 'error') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* honest scope notice */}
          <div className="pf-mgmt-panel" style={{ alignItems: 'flex-start', gap: 8 }}>
            <ShieldQuestion size={16} style={{ marginTop: 1, flexShrink: 0, color: 'var(--fg-muted)' }} />
            <span className="pf-mgmt-panel__body">
              <span className="pf-mgmt-panel__s">{t('mgmt.pl.scope')}</span>
            </span>
          </div>

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--fg)' }}>
            <input type="checkbox" checked={required} onChange={(e) => setRequired((e.target as HTMLInputElement).checked)} />
            {t('mgmt.pl.require')}
          </label>

          {required && (
            <>
              <label style={fieldLabel}>
                {t('mgmt.pl.linkLabel')}
                <input type="url" inputMode="url" placeholder="https://buy.stripe.com/…" value={url} onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
                  className="pf-input" style={{ ...inputStyle, borderColor: urlValid ? 'var(--border)' : 'var(--color-danger)' }} />
                {!urlValid && <span style={{ fontSize: 11.5, color: 'var(--color-danger)' }}>{t('mgmt.pl.httpsErr')}</span>}
              </label>
              <label style={fieldLabel}>
                {t('mgmt.pl.amountLabel')}
                <input type="text" placeholder={t('mgmt.pl.amountPh')} value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} className="pf-input" style={inputStyle} />
              </label>
              <label style={fieldLabel}>
                {t('mgmt.av.instructions')}
                <textarea placeholder={t('mgmt.pl.instrPh')} value={instructions} onInput={(e) => setInstructions((e.target as HTMLTextAreaElement).value)} rows={2} className="pf-input" style={{ ...inputStyle, resize: 'vertical' }} />
              </label>
            </>
          )}

          {/* §Batch 2c — manual tax & shipping (applies to the cart total; commerce) */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="pf-mgmt-panel__t" style={{ fontSize: 13 }}>{t('mgmt.pl.taxShipT')} <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>{t('mgmt.pl.taxShipOpt')}</span></span>
            <span className="pf-mgmt-panel__s" style={{ fontSize: 12 }}>{t('mgmt.pl.taxShipS')}</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={fieldLabel}>
                {t('mgmt.pl.taxRate')}
                <input type="number" min={0} max={100} step={0.01} inputMode="decimal" placeholder="8.75" value={tax} onInput={(e) => setTax((e.target as HTMLInputElement).value)}
                  className="pf-input" style={{ ...inputStyle, borderColor: taxValid ? 'var(--border)' : 'var(--color-danger)' }} />
                {!taxValid && <span style={{ fontSize: 11.5, color: 'var(--color-danger)' }}>{t('mgmt.pl.taxRange')}</span>}
              </label>
              <label style={fieldLabel}>
                {t('mgmt.pl.taxLabel')}
                <input type="text" placeholder={t('mgmt.pl.taxPh')} value={taxLabel} onInput={(e) => setTaxLabel((e.target as HTMLInputElement).value)} className="pf-input" style={inputStyle} />
              </label>
              <label style={fieldLabel}>
                {t('mgmt.pl.shipFee')}
                <input type="number" min={0} step={0.01} inputMode="decimal" placeholder="5.00" value={shipping} onInput={(e) => setShipping((e.target as HTMLInputElement).value)}
                  className="pf-input" style={{ ...inputStyle, borderColor: shipValid ? 'var(--border)' : 'var(--color-danger)' }} />
                {!shipValid && <span style={{ fontSize: 11.5, color: 'var(--color-danger)' }}>{t('mgmt.pl.shipMin')}</span>}
              </label>
              <label style={fieldLabel}>
                {t('mgmt.pl.shipLabel')}
                <input type="text" placeholder={t('mgmt.pl.shipPh')} value={shippingLabel} onInput={(e) => setShippingLabel((e.target as HTMLInputElement).value)} className="pf-input" style={inputStyle} />
              </label>
            </div>
          </div>

          {phase.k === 'error' && (
            <p style={{ fontSize: 12.5, color: 'var(--color-danger)', margin: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <AlertCircle size={13} /> {phase.msg}
            </p>
          )}

          <div className="pf-row">
            <span />
            <button type="button" className="pf-btn pf-btn--grad pf-btn--sm" disabled={!canSave || phase.k === 'saving'} onClick={() => void save()}>
              {phase.k === 'saving' ? <><Loader2 size={13} className="pf-spin" /> {t('mgmt.saving')}</>
                : phase.k === 'saved' ? <><Check size={13} /> {t('mgmt.saved')}</>
                : t('mgmt.pl.save')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const fieldLabel: JSX.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--fg)' };
const inputStyle: JSX.CSSProperties = { padding: '9px 11px', borderRadius: 'var(--r-md)', border: '1px solid var(--border)', background: 'var(--bg-el)', color: 'var(--fg)', fontSize: 13.5, fontWeight: 400 };
